/* ll-theme.js (owner: stage 0) — Llama Land chrome behaviour, every template that uses layout/theme.liquid.
   Plain ES2017, no dependencies, no GSAP, loaded with defer. Progressive: with JS off the menu button does nothing
   harmful, the bag is a link to /cart, the newsletter is a native Shopify customer form.

   1. Menu drawer (<dialog>): open/close, Esc + backdrop + link clicks close, focus returns to the Menu button.
   2. Bag: opens UpCart (window.upcartOpenCart) when present, else the link goes to /cart; count kept in sync.
   3. UpCart plan observer: a byte-for-byte behavioural copy of setupUpcartSubscriptionObserver from the live compiled
      header module (assets/header-rs-vite-build-1GzF-P9F.js), which stops running once the old header section is
      disabled. It removes UpCart's "Dynamic Box Subscription / Every 30 Days" option from the subscription upgrade
      dropdown and, if that option was selected (or nothing was), selects the next available plan.
   4. Header: --height-header / --ll-hdr-h on <html> (live CSS still reads --height-header); homepage band → solid.
   5. Brand rating: optional live refresh from Okendo's public store aggregate (same source as /pages/review).
   6. iOS Safari toolbar tint after UpCart closes: UpCart's backdrop (shadow DOM, rgb(0,0,0) at opacity .4, fixed and
      full-screen) makes Safari 26 tint the bottom toolbar black, and Safari keeps that tint after the backdrop is
      display:none until the page scrolls. When body.upcartPopupShow is removed we nudge the scroll 1 px and back, which
      makes Safari re-sample the page. iOS only (body.is-ios-device), no layout change. */
(function () {
  'use strict';
  var D = document, H = D.documentElement;

  /* ---------- 1. menu drawer ---------- */
  function initDrawer(root) {
    var dialog = root.querySelector('[data-ll-drawer]');
    var openers = root.querySelectorAll('[data-ll-drawer-open]');
    if (!dialog || dialog.dataset.llReady) return;
    dialog.dataset.llReady = '1';
    function setExpanded(v) { openers.forEach(function (b) { b.setAttribute('aria-expanded', v ? 'true' : 'false'); }); }
    function close() { if (dialog.open) dialog.close(); }
    openers.forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (typeof dialog.showModal !== 'function') return;
        dialog.showModal();
        H.classList.add('ll-drawer-open');
        setExpanded(true);
      });
    });
    dialog.addEventListener('close', function () {
      H.classList.remove('ll-drawer-open');
      setExpanded(false);
      if (openers[0]) openers[0].focus();
    });
    dialog.querySelectorAll('[data-ll-drawer-close]').forEach(function (b) { b.addEventListener('click', close); });
    dialog.addEventListener('click', function (e) { if (e.target === dialog) close(); });   // backdrop
    dialog.querySelectorAll('a[href*="#"]').forEach(function (a) { a.addEventListener('click', close); });
  }

  /* ---------- 2. bag: UpCart open + count ---------- */
  function setCount(n) {
    n = Number(n) || 0;
    D.querySelectorAll('[data-ll-cart-count]').forEach(function (el) { el.textContent = n; el.hidden = !(n > 0); });
  }
  function refreshCount() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) { if (cart) setCount(cart.item_count); })
      .catch(function () {});
  }
  function initCart() {
    D.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest('[data-ll-cart-open]');
      if (!link || typeof window.upcartOpenCart !== 'function') return;   // no UpCart: the link goes to /cart
      e.preventDefault();
      var d = link.closest('dialog[open]');
      if (d) d.close();
      window.upcartOpenCart();
    });
    ['aftersell-upcart:public-events:cart-updated', 'aftersell-upcart:public-events:cart-item-added',
      'aftersell-upcart:public-events:cart-item-removed'].forEach(function (ev) {
      window.addEventListener(ev, refreshCount);
      D.addEventListener(ev, refreshCount);
    });
    D.addEventListener('apps:product-added-to-cart', refreshCount);   // the old header listened to this too
    D.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) setCount(e.detail.cart.item_count); else refreshCount();
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCount(); });   // bfcache
  }

  /* ---------- 3. UpCart subscription dropdown observer (copied from the live header module) ---------- */
  var upcart = { shadowObserver: null, bodyObserver: null, interval: 0 };
  function setupUpcartSubscriptionObserver() {
    var clean = function () {
      var host = D.querySelector('#upCart');
      if (!host || !host.shadowRoot) return;
      var root = host.shadowRoot;
      var select = root.querySelector('.upcart-subscription-upgrade-dropdown');
      if (!select) return;
      var bad = root.querySelector('[data-internal-properties*="sellingPlanGroupName=Dynamic Box Subscription"][data-internal-properties*="sellingPlanName=Every 30 Days"]');
      if (bad) {
        var current = select.value, wasSelected = bad.value === current;
        bad.remove();
        console.log('Removed unwanted subscription option from shadow DOM');
        if (wasSelected || !select.value) {
          var options = Array.from(select.options).filter(function (o) { return o.value && o.value !== '' && !o.disabled; });
          if (options.length > 0) {
            select.value = options[0].value;
            select.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
            select.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
            console.log('Auto-selected next available plan: ' + options[0].textContent);
          }
        }
      }
    };
    var watchShadow = function (shadowRoot) {
      if (upcart.shadowObserver) upcart.shadowObserver.disconnect();
      upcart.shadowObserver = new MutationObserver(function (mutations) {
        var hit = false;
        mutations.forEach(function (m) {
          if (m.type === 'childList' && m.addedNodes.length > 0) {
            m.addedNodes.forEach(function (n) {
              if (n.nodeType === Node.ELEMENT_NODE && ((n.classList && n.classList.contains('upcart-subscription-upgrade-dropdown')) ||
                (n.querySelector && n.querySelector('.upcart-subscription-upgrade-dropdown')) || n.tagName === 'OPTION')) hit = true;
            });
          }
          if (m.type === 'attributes' && ((m.target.closest && m.target.closest('.upcart-subscription-upgrade-dropdown')) || m.target.tagName === 'SELECT')) hit = true;
        });
        if (hit) setTimeout(clean, 0);
      });
      upcart.shadowObserver.observe(shadowRoot, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-internal-properties', 'value', 'selected'] });
      shadowRoot.addEventListener('change', function (e) { if (e.target.closest('.upcart-subscription-upgrade-dropdown')) setTimeout(clean, 100); });
      shadowRoot.addEventListener('focus', function (e) { if (e.target.closest('.upcart-subscription-upgrade-dropdown')) setTimeout(clean, 0); }, true);
    };
    var attach = function () {
      var host = D.querySelector('#upCart');
      if (host && host.shadowRoot) { clean(); watchShadow(host.shadowRoot); return true; }
      return false;
    };
    if (!attach()) {
      upcart.bodyObserver = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          if (m.type === 'childList' && m.addedNodes.length > 0) {
            m.addedNodes.forEach(function (n) {
              if (n.nodeType === Node.ELEMENT_NODE && (n.id === 'upCart' || (n.querySelector && n.querySelector('#upCart')))) {
                setTimeout(function () { if (attach()) upcart.bodyObserver.disconnect(); }, 100);
              }
            });
          }
        });
      });
      upcart.bodyObserver.observe(D.body, { childList: true, subtree: true });
    }
    upcart.interval = setInterval(function () {
      if (attach()) { clearInterval(upcart.interval); if (upcart.bodyObserver) upcart.bodyObserver.disconnect(); }
    }, 1000);
  }
  /* Run it only when the old header is not on the page (it would run its own copy). */
  function needsObserver() { return !D.querySelector('[data-section-type="header"]'); }

  /* ---------- 4. header height + homepage band ---------- */
  function initHeader(root) {
    var hdr = root.querySelector('[data-ll-hdr]');
    if (!hdr) return;
    var setH = function () {
      var h = Math.ceil(hdr.getBoundingClientRect().height);
      H.style.setProperty('--height-header', h + 'px');
      H.style.setProperty('--ll-hdr-h', h + 'px');
    };
    setH();
    if ('ResizeObserver' in window) new ResizeObserver(setH).observe(hdr); else window.addEventListener('resize', setH);
    if (!hdr.hasAttribute('data-ll-hdr-band')) return;
    var hero = D.querySelector('[data-ll-hero]');
    if (!hero) { hdr.classList.add('is-solid'); return; }
    var ticking = false;
    var update = function () {
      ticking = false;
      var r = hero.getBoundingClientRect();
      // solid once 70% of the hero has scrolled past (Option C: .is-solid past 70% of the hero)
      hdr.classList.toggle('is-solid', r.bottom < r.height * 0.3 + hdr.offsetHeight);
    };
    window.addEventListener('scroll', function () { if (!ticking) { ticking = true; requestAnimationFrame(update); } }, { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  /* ---------- 5. brand rating, live from Okendo (the /pages/review number) ---------- */
  function initBrandRating() {
    var els = D.querySelectorAll('[data-ll-brand-rating][data-okendo-store]');
    if (!els.length) return;
    var store = els[0].getAttribute('data-okendo-store');
    if (!store) return;
    fetch('https://api.okendo.io/v1/stores/' + encodeURIComponent(store) + '/review_aggregate', { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var a = d && d.reviewAggregate;
        if (!a || !a.reviewCount || !a.reviewRatingValuesTotal) return;
        var avg = (Math.round((a.reviewRatingValuesTotal / a.reviewCount) * 10) / 10).toFixed(1);
        var count = Number(a.reviewCount).toLocaleString('en-US');
        els.forEach(function (el) {
          var v = el.querySelector('[data-ll-rating-value]'); if (v) v.textContent = avg;
          var c = el.querySelector('[data-ll-rating-count]'); if (c) c.textContent = count;
        });
      })
      .catch(function () {});   // keep the settings values
  }

  /* ---------- 6. iOS: re-tint Safari's toolbar after UpCart closes ---------- */
  function initUpcartTintFix() {
    var B = D.body;
    if (!B || !B.classList.contains('is-ios-device') || !('MutationObserver' in window)) return;
    var wasOpen = B.classList.contains('upcartPopupShow');
    new MutationObserver(function () {
      var isOpen = B.classList.contains('upcartPopupShow');
      if (wasOpen && !isOpen) {
        setTimeout(function () {
          var y = window.pageYOffset;
          window.scrollTo(0, y > 0 ? y - 1 : y + 1);
          requestAnimationFrame(function () { window.scrollTo(0, y); });
        }, 350);   // after UpCart's close transition
      }
      wasOpen = isOpen;
    }).observe(B, { attributes: true, attributeFilter: ['class'] });
  }

  function boot() {
    D.querySelectorAll('[data-ll-header]').forEach(function (root) { initDrawer(root); initHeader(root); });
    initCart();
    if (needsObserver()) setupUpcartSubscriptionObserver();
    initBrandRating();
    initUpcartTintFix();
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();

  // Theme editor: re-init a re-rendered section.
  D.addEventListener('shopify:section:load', function (e) {
    var t = e.target; if (!t) return;
    t.querySelectorAll('[data-ll-header]').forEach(function (root) { initDrawer(root); initHeader(root); });
    initBrandRating();
  });
})();
